// react-table depends on spreading props; we could name them explicitly but this component might
// break if the shape of the return value from row.getRowProps() changes
/* eslint-disable react/jsx-props-no-spreading */
// react-table is extending from object, so we have to do that as well
/* eslint-disable @typescript-eslint/ban-types */
import React from 'react';
import cx from 'classnames';
import { Cell, Row } from 'react-table';

interface SubRowProps<D extends object> {
    /**
     * Index to apply as the `aria-rowindex` attribute - see {@link https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/Row_Role}
     */
    ariaRowIndex: number;
    /**
     * Row height
     */
    height: number;
    /**
     * The react-table row instance
     */
    row: Row<D>;
    /**
     * Tailwind size to apply to text
     */
    textSize: 'xs' | 'sm' | 'base' | 'lg';
}

/**
 * Renders the content for a sub-row within a grouped row. These are the lowest level rows; this is
 * a normal row in a table without grouped rows.
 * @param props - {@link SubRowProps}
 */
export function SubRow<D extends object>({
    ariaRowIndex,
    height,
    row,
    textSize,
}: SubRowProps<D>): JSX.Element {
    const evenRow = ariaRowIndex % 2 === 0;
    const { key: rowKey, ...rowRest } = row.getRowProps();

    return (
        <div
            key={rowKey}
            {...rowRest}
            aria-rowindex={ariaRowIndex}
            className={cx('tr relative flex items-center', {
                'even-row': evenRow,
            })}
            style={{
                height,
            }}
        >
            {row.cells.map((cell: Cell<D>) => {
                const { key: cellKey, ...cellRest } = cell.getCellProps();
                const { isAggregated, isGrouped, isPlaceholder } = cell;
                const showGrouped = isGrouped;
                const showAggregated = !isGrouped && isAggregated;
                const showCell = !isGrouped && !isAggregated && !isPlaceholder;

                return (
                    <div
                        key={cellKey}
                        {...cellRest}
                        className={cx(`td text-${textSize}`, {
                            'font-medium': !showCell,
                        })}
                    >
                        {showGrouped && <>{cell.render('Grouped')}</>}
                        {showAggregated && <>{cell.render('Aggregated')}</>}
                        {showCell && <>{cell.render('Cell')}</>}
                    </div>
                );
            })}
        </div>
    );
}
